import { useEffect, useState } from 'react';
import { admin_api } from '@/lib/api';
import { toast } from 'sonner';
import { Trash, Plus, PencilSimple, Van, X } from '@phosphor-icons/react';

const EMPTY = { name: '', type: 'Standard', capacity: '4', features: '', image_url: '', rego: '', status: 'available', notes: '', rego_expiry: '', insurance_expiry: '', next_service_date: '', hoist_inspection_due: '', odometer_km: '' };
const TYPES = ['Standard', 'Wheelchair', 'Group', 'Group / Wheelchair'];
const STATUSES = ['available', 'in_service', 'maintenance', 'cleaning', 'breakdown', 'retired'];
const STATUS_STYLE = {
  available: 'bg-green-100 text-green-700',
  in_service: 'bg-blue-100 text-blue-700',
  maintenance: 'bg-amber-100 text-amber-700',
  cleaning: 'bg-cyan-100 text-cyan-700',
  breakdown: 'bg-red-100 text-red-600',
  retired: 'bg-neutral-200 text-neutral-600',
};

export default function AdminFleet() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const res = await admin_api.fleetList(); setItems(res.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const toPayload = () => ({
    ...form,
    features: form.features.split(',').map(s => s.trim()).filter(Boolean),
    odometer_km: form.odometer_km === '' ? null : Number(form.odometer_km),
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await admin_api.fleetUpdate(editingId, toPayload());
        toast.success('Vehicle updated.');
      } else {
        await admin_api.fleetCreate(toPayload());
        toast.success('Vehicle added.');
      }
      setForm(EMPTY); setEditingId(null); load();
    } catch { toast.error('Could not save vehicle.'); }
  };

  const edit = (v) => {
    setEditingId(v.id);
    setForm({
      name: v.name, type: v.type, capacity: v.capacity,
      features: (v.features || []).join(', '),
      image_url: v.image_url || '', rego: v.rego || '',
      status: v.status, notes: v.notes || '',
      rego_expiry: v.rego_expiry || '', insurance_expiry: v.insurance_expiry || '',
      next_service_date: v.next_service_date || '', hoist_inspection_due: v.hoist_inspection_due || '',
      odometer_km: v.odometer_km ?? '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const setStatus = async (v, status) => {
    try {
      await admin_api.fleetUpdate(v.id, { ...v, status });
      toast.success(`${v.name} → ${status}`);
      load();
    } catch { toast.error('Could not update status.'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try { await admin_api.fleetDelete(id); toast.success('Deleted.'); load(); }
    catch { toast.error('Could not delete.'); }
  };

  return (
    <div data-testid="admin-fleet">
      <div className="mb-8">
        <div className="overline mb-2">Operations</div>
        <h1 className="font-display font-extrabold text-3xl text-ink">Fleet management</h1>
        <p className="text-ink-muted text-sm mt-1">Vehicles marked active appear on the public Fleet page. Maintenance and retired vehicles are hidden.</p>
      </div>

      <form onSubmit={submit} className="p-6 rounded-2xl bg-white border border-neutral-200 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-fleet-form">
        <div className="md:col-span-2 flex items-center gap-2 text-sm font-semibold text-ink">
          <Van size={18} weight="duotone" className="text-brand" />
          {editingId ? 'Edit vehicle' : 'Add a vehicle'}
          {editingId && (
            <button type="button" onClick={cancelEdit} data-testid="admin-fleet-cancel-edit" className="ml-auto inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink">
              <X size={12} /> Cancel edit
            </button>
          )}
        </div>
        <div>
          <label className="label-base">Vehicle name</label>
          <input required value={form.name} onChange={set('name')} data-testid="admin-fleet-name" className="input-base" placeholder="e.g. Accessible Wheelchair Van" />
        </div>
        <div>
          <label className="label-base">Type</label>
          <select value={form.type} onChange={set('type')} data-testid="admin-fleet-type" className="input-base">
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="label-base">Capacity</label>
          <input required value={form.capacity} onChange={set('capacity')} data-testid="admin-fleet-capacity" className="input-base" placeholder="e.g. 1WC + 3" />
        </div>
        <div>
          <label className="label-base">Rego (optional)</label>
          <input value={form.rego} onChange={set('rego')} data-testid="admin-fleet-rego" className="input-base" placeholder="e.g. 1AB 2CD" />
        </div>
        <div className="md:col-span-2">
          <label className="label-base">Features (comma-separated)</label>
          <input value={form.features} onChange={set('features')} data-testid="admin-fleet-features" className="input-base" placeholder="Rear-entry ramp, 4-point tie-downs, Grab-rails" />
        </div>
        <div className="md:col-span-2">
          <label className="label-base">Image URL (optional)</label>
          <input value={form.image_url} onChange={set('image_url')} data-testid="admin-fleet-image" className="input-base" placeholder="https://…" />
        </div>
        <div>
          <label className="label-base">Status</label>
          <select value={form.status} onChange={set('status')} data-testid="admin-fleet-status" className="input-base">
            {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="label-base">Odometer (km)</label>
          <input type="number" value={form.odometer_km} onChange={set('odometer_km')} data-testid="admin-fleet-odometer" className="input-base" placeholder="e.g. 84500" />
        </div>
        <div><label className="label-base">Rego expiry</label><input type="date" value={form.rego_expiry} onChange={set('rego_expiry')} data-testid="admin-fleet-rego-expiry" className="input-base" /></div>
        <div><label className="label-base">Insurance expiry</label><input type="date" value={form.insurance_expiry} onChange={set('insurance_expiry')} data-testid="admin-fleet-insurance-expiry" className="input-base" /></div>
        <div><label className="label-base">Next service</label><input type="date" value={form.next_service_date} onChange={set('next_service_date')} data-testid="admin-fleet-service-date" className="input-base" /></div>
        <div><label className="label-base">Hoist inspection due</label><input type="date" value={form.hoist_inspection_due} onChange={set('hoist_inspection_due')} data-testid="admin-fleet-hoist-due" className="input-base" /></div>
        <div className="flex items-end md:col-span-2">
          <button type="submit" data-testid="admin-fleet-submit" className="btn-primary !py-2.5 !px-5 text-sm w-full md:w-auto md:ml-auto">
            {editingId ? <><PencilSimple size={14} weight="bold" /> Save changes</> : <><Plus size={14} weight="bold" /> Add vehicle</>}
          </button>
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-fleet-list">
        {loading && <div className="text-ink-muted">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-ink-muted">No vehicles yet.</div>}
        {items.map(v => (
          <div key={v.id} className="rounded-2xl bg-white border border-neutral-200 overflow-hidden" data-testid={`admin-fleet-card-${v.id}`}>
            {v.image_url && (
              <div className="aspect-[16/8] bg-neutral-100 overflow-hidden">
                <img src={v.image_url} alt={v.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${STATUS_STYLE[v.status] || ''}`}>{v.status.replace('_', ' ')}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => edit(v)} data-testid={`admin-fleet-edit-${v.id}`} className="text-ink-muted hover:text-brand" aria-label="Edit">
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => del(v.id)} data-testid={`admin-fleet-delete-${v.id}`} className="text-ink-muted hover:text-red-500" aria-label="Delete">
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              <div className="font-display font-bold text-lg text-ink">{v.name}</div>
              <div className="text-xs text-ink-muted mt-0.5">{v.type} · Capacity {v.capacity}{v.rego ? ` · ${v.rego}` : ''}{v.odometer_km ? ` · ${Number(v.odometer_km).toLocaleString()} km` : ''}</div>
              <div className="mt-2 space-y-0.5 text-[11px] text-ink-soft">
                {v.rego_expiry && <div>Rego expires {v.rego_expiry}</div>}
                {v.insurance_expiry && <div>Insurance expires {v.insurance_expiry}</div>}
                {v.next_service_date && <div>Service due {v.next_service_date}</div>}
                {v.hoist_inspection_due && <div>Hoist inspection {v.hoist_inspection_due}</div>}
                {v.maintenance_history?.length > 0 && <div className="text-ink-muted">{v.maintenance_history.length} maintenance record(s)</div>}
              </div>
              {v.features?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {v.features.map(f => (
                    <span key={f} className="text-[11px] bg-neutral-100 text-ink-soft rounded-full px-2.5 py-1">{f}</span>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <select
                  value={v.status}
                  onChange={e => setStatus(v, e.target.value)}
                  data-testid={`admin-fleet-status-select-${v.id}`}
                  className="input-base !py-1.5 text-xs"
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
